import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, API_BASE } from '../../lib/api';
import { toast } from 'sonner';
import { UploadSimple, X, ImageSquare, LinkSimple } from '@phosphor-icons/react';

const ACCEPT = 'image/png,image/jpeg,image/jpg,image/webp';
const MAX_BYTES = 5 * 1024 * 1024; // 5MB
const ALLOWED_EXT = ['png', 'jpg', 'jpeg', 'webp'];

/**
 * Drag-and-drop image uploader.
 * Uploads via the existing /files/upload endpoint (purpose=work_sample), then
 * passes back the authenticated download URL so it can be saved as a
 * candidate project's `thumbnail_url`.
 *
 * Falls back to a "paste URL" mode for users who already have an image hosted
 * elsewhere (Behance, personal site, S3, etc.).
 */
const ImageDropzone = ({ value, onChange, testIdPrefix = 'thumb' }) => {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pasteMode, setPasteMode] = useState(false);
  const [pasteValue, setPasteValue] = useState('');
  const fileInputRef = useRef();

  useEffect(() => {
    if (value && !value.startsWith('blob:')) setPasteValue(value);
  }, [value]);

  const upload = useCallback(async (file) => {
    if (!file) return;
    if (!ACCEPT.split(',').includes(file.type)) {
      toast.error('Only PNG, JPG, or WEBP images.');
      return;
    }
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      toast.error('Only PNG, JPG, or WEBP images.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('Max 5MB.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/files/upload?purpose=work_sample', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const token = typeof window !== 'undefined' ? localStorage.getItem('hikari_token') : '';
      const url = `${API_BASE}/files/download/${data.id}?auth=${encodeURIComponent(token || '')}`;
      onChange(url);
      toast.success('Thumbnail uploaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [onChange]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  };

  const onFilePick = (e) => {
    const file = e.target.files?.[0];
    if (file) upload(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onPasteCommit = () => {
    const v = pasteValue.trim();
    if (!v) return onChange('');
    onChange(v);
    toast.success('Thumbnail link saved');
  };

  // ---------- Render ----------
  // Has-image preview
  if (value && !pasteMode) {
    return (
      <div className="space-y-2" data-testid={`${testIdPrefix}-dropzone`}>
        <div
          className="relative w-full bg-secondary/40 border border-border overflow-hidden"
          style={{ aspectRatio: '16/9' }}
        >
          <img
            src={value}
            alt="Project thumbnail preview"
            className="w-full h-full object-cover"
            data-testid={`${testIdPrefix}-preview`}
            onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
          />
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute top-2 right-2 bg-foreground text-background p-1.5 hover:bg-destructive"
            aria-label="Remove thumbnail"
            data-testid={`${testIdPrefix}-remove`}
          >
            <X size={12} />
          </button>
        </div>
        <div className="flex items-center justify-between gap-2 mono text-[11px] text-muted-foreground">
          <span className="truncate">Thumbnail set</span>
          <button
            type="button"
            onClick={() => setPasteMode(true)}
            className="label-eyebrow hover:text-foreground"
            data-testid={`${testIdPrefix}-edit-url`}
          >
            Edit URL
          </button>
        </div>
      </div>
    );
  }

  // Paste-URL mode
  if (pasteMode) {
    return (
      <div className="space-y-2" data-testid={`${testIdPrefix}-paste-mode`}>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={pasteValue}
            onChange={(e) => setPasteValue(e.target.value)}
            placeholder="https://..."
            className="flex-1 h-11 px-3 border border-border rounded-none focus:outline-none focus:border-foreground"
            data-testid={`${testIdPrefix}-paste-input`}
          />
          <button
            type="button"
            onClick={() => { onPasteCommit(); setPasteMode(false); }}
            className="h-11 px-3 bg-foreground text-background hover:bg-foreground/90 label-eyebrow"
            data-testid={`${testIdPrefix}-paste-save`}
          >
            Save
          </button>
        </div>
        <button
          type="button"
          onClick={() => setPasteMode(false)}
          className="label-eyebrow text-muted-foreground hover:text-foreground"
        >
          ← Back to drag-and-drop
        </button>
      </div>
    );
  }

  // Empty dropzone
  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-dropzone`}>
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`w-full flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed transition-colors ${
          dragOver
            ? 'border-foreground bg-secondary'
            : 'border-border bg-secondary/30 hover:border-foreground'
        }`}
        style={{ aspectRatio: '16/9' }}
        data-testid={`${testIdPrefix}-drop-target`}
        disabled={uploading}
      >
        {uploading ? (
          <>
            <UploadSimple size={32} className="text-primary animate-pulse" />
            <div className="mono text-xs text-muted-foreground">Uploading…</div>
          </>
        ) : (
          <>
            <ImageSquare size={32} className="text-muted-foreground" />
            <div className="text-sm font-medium">Drop a thumbnail here</div>
            <div className="mono text-[11px] text-muted-foreground">
              or click to pick · PNG / JPG / WEBP · 5MB max
            </div>
          </>
        )}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        onChange={onFilePick}
        className="hidden"
        data-testid={`${testIdPrefix}-file-input`}
      />
      <div className="flex items-center justify-between mono text-[11px] text-muted-foreground">
        <span>Optional — for portfolio shots, posters, brand banners.</span>
        <button
          type="button"
          onClick={() => setPasteMode(true)}
          className="label-eyebrow hover:text-foreground flex items-center gap-1"
          data-testid={`${testIdPrefix}-switch-to-paste`}
        >
          <LinkSimple size={11} /> Paste URL instead
        </button>
      </div>
    </div>
  );
};

export default ImageDropzone;
